import { Activity, AlertOctagon, CheckCircle2, Loader2, ShieldOff } from "lucide-react";
import { useFetch } from "./api/useFetch";
import { adaptHealth } from "./api/adapters";
import { isSurfaceLive } from "./api/platformClient";
import { LiveBadge } from "./ui-badges";

const TONES = {
  ok: { ring: "ring-emerald-200", bg: "bg-emerald-50", text: "text-emerald-700", icon: CheckCircle2, label: "Healthy" },
  healthy: { ring: "ring-emerald-200", bg: "bg-emerald-50", text: "text-emerald-700", icon: CheckCircle2, label: "Healthy" },
  degraded: { ring: "ring-amber-200", bg: "bg-amber-50", text: "text-amber-800", icon: AlertOctagon, label: "Degraded" },
  down: { ring: "ring-rose-200", bg: "bg-rose-50", text: "text-rose-700", icon: AlertOctagon, label: "Down" },
};

const fmtUptime = (sec) => {
  if (!sec) return "—";
  const days = Math.floor(sec / 86400);
  const hours = Math.floor((sec % 86400) / 3600);
  if (days) return `${days}d ${hours}h`;
  const minutes = Math.floor((sec % 3600) / 60);
  if (hours) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

export const PlatformHealth = () => {
  const live = isSurfaceLive("HEALTH");
  const { data, error, loading } = useFetch(live ? "/api/platform/health" : null);

  return (
    <div className="space-y-4" data-testid="platform-health">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">
            Platform health
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Status of the ShelfLife preview backend powering the Control Center.
          </p>
        </div>
        <LiveBadge live={live} />
      </div>

      {!live && (
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-center">
          <ShieldOff className="mx-auto h-8 w-8 text-slate-400" />
          <p className="mt-3 text-sm text-slate-600">
            Live health is disabled via the <span className="font-mono">REACT_APP_LIVE_HEALTH</span> flag.
          </p>
        </div>
      )}

      {live && loading && (
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-center" data-testid="platform-health-loading">
          <Loader2 className="mx-auto h-6 w-6 text-slate-400 animate-spin" />
          <p className="mt-3 text-sm text-slate-500">Checking…</p>
        </div>
      )}

      {live && error && error.status === 404 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-6" data-testid="platform-health-disabled">
          <div className="flex items-center gap-3">
            <AlertOctagon className="h-5 w-5 text-amber-700" />
            <div className="text-sm font-semibold text-amber-900">
              Control Center disabled in this environment
            </div>
          </div>
          <p className="text-xs text-amber-800 mt-2">
            The ShelfLife preview returned 404 for <span className="font-mono">/api/platform/health</span>.
            The platform module may be turned off for this environment.
          </p>
        </div>
      )}

      {live && error && error.status !== 404 && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-6" data-testid="platform-health-error">
          <div className="flex items-center gap-3">
            <AlertOctagon className="h-5 w-5 text-rose-700" />
            <div className="text-sm font-semibold text-rose-900">
              {error.kind === "forbidden" ? "Not authorized" :
               error.kind === "unauthorized" ? "Session expired" :
               error.kind === "network" ? "Cannot reach preview API" :
               "Health check failed"}
            </div>
          </div>
          <p className="text-xs text-rose-800 mt-2">{error.message}</p>
        </div>
      )}

      {live && data && !error && (
        <HealthCard h={adaptHealth(data)} />
      )}
    </div>
  );
};

const HealthCard = ({ h }) => {
  const tone = TONES[h.status] || TONES.ok;
  const Icon = tone.icon;
  return (
    <div className={`rounded-xl border ${tone.ring} bg-white p-6 ring-1 ring-inset`}>
      <div className="flex items-center gap-3">
        <span className={`flex h-10 w-10 items-center justify-center rounded-lg ${tone.bg} ${tone.text}`}>
          <Icon className="h-5 w-5" />
        </span>
        <div>
          <div className={`text-base font-semibold ${tone.text}`}>{tone.label}</div>
          <div className="text-xs text-slate-500">as of {h.asOf}</div>
        </div>
      </div>
      <dl className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <Cell label="Operator" value={h.operatorEmail || "—"} />
        <Cell label="Role" value={h.role || "—"} />
        <Cell label="Module flag" value={h.flagEnabled ? "Enabled" : "Disabled"} tone={h.flagEnabled ? "ok" : "down"} />
        <Cell label="Server time" value={h.asOf} />
      </dl>
    </div>
  );
};

const Cell = ({ label, value, tone }) => (
  <div className="rounded-lg bg-slate-50 p-3">
    <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
    <div className={`mt-0.5 text-sm font-semibold ${tone === "down" ? "text-rose-700" : "text-slate-900"}`}>
      {value}
    </div>
  </div>
);
